import {Check, Refresh} from '@mui/icons-material';
import {
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
} from '@mui/material';
import {useMemo, useState} from 'react';
import {useTranslation} from 'tolgee';

import {syncCustomers} from '~api/users';
import {switchCustomerAndReload} from '~utils/customerSwitch';
import {selectAvailableCustomers, selectCustomer} from '~redux/reducers/userReducer';
import {setUserData} from '~redux/actions/userActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {ClosableDialog} from '~components/common/ClosableDialog';
import {Spinner} from '~components/common/Spinner';
import {ABOVE_ASK_AI_PANEL_Z_INDEX} from '~components/ask-ai/askAiAgents';
import {SearchIcon} from 'src/icons';

interface CustomerSwitcherDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CustomerSwitcherDialog({open, onClose}: CustomerSwitcherDialogProps) {
  const {t} = useTranslation('mainMenu');
  const dispatch = useAppDispatch();
  const currentCustomer = useAppSelector(selectCustomer);
  const availableCustomers = useAppSelector(selectAvailableCustomers);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const filteredCustomers = useMemo(
    () =>
      availableCustomers
        .filter((c) => c.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.localeCompare(b)),
    [availableCustomers, search],
  );

  const handleCustomerSelect = async (customer: string) => {
    if (customer === currentCustomer || isSwitching) return;

    setIsSwitching(true);
    try {
      // Full page reload to the archive page guarantees a clean slate.
      await switchCustomerAndReload(customer);
    } catch (error) {
      console.error('Failed to switch customer:', error);
      setIsSwitching(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const userData = await syncCustomers();
      dispatch(setUserData(userData));
    } catch (error) {
      console.error('Failed to refresh customers:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  return (
    <ClosableDialog
      open={open}
      hasCloseButton
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      sx={{zIndex: ABOVE_ASK_AI_PANEL_Z_INDEX}}
      data-testid='customer-switcher-dialog'
    >
      <DialogTitle>{t('switchCustomer')}</DialogTitle>
      <DialogContent sx={{paddingY: 3}}>
        {isSwitching ? (
          <Spinner />
        ) : (
          <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <TextField
                size='small'
                fullWidth
                placeholder={t('searchCustomer')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={t('refreshCustomers')} PopperProps={{sx: {zIndex: ABOVE_ASK_AI_PANEL_Z_INDEX}}}>
                <IconButton onClick={handleRefresh} disabled={isRefreshing} size='small'>
                  <Refresh
                    fontSize='small'
                    sx={
                      isRefreshing
                        ? {
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              from: {transform: 'rotate(0deg)'},
                              to: {transform: 'rotate(360deg)'},
                            },
                          }
                        : undefined
                    }
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <List disablePadding sx={{maxHeight: 400, overflow: 'auto'}}>
              {filteredCustomers.map((customer) => (
                <ListItemButton
                  key={customer}
                  onClick={() => handleCustomerSelect(customer)}
                  selected={customer === currentCustomer}
                >
                  <ListItemText primary={customer} />
                  {customer === currentCustomer && (
                    <ListItemIcon sx={{minWidth: 'unset'}}>
                      <Check fontSize='small' />
                    </ListItemIcon>
                  )}
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
    </ClosableDialog>
  );
}
